import type {
  AdvertisingAd,
  AdvertisingValues,
} from '@borradh-workspace/api-client/types';

const currencies = new Map<string, Intl.NumberFormat>();
const integer = new Intl.NumberFormat('en-IE', { maximumFractionDigits: 0 });
const decimal = new Intl.NumberFormat('en-IE', { maximumFractionDigits: 2 });
export const formatMoney = (
  value: number | null,
  currency: string | null = 'EUR'
) => {
  if (value === null || !currency) return '—';
  try {
    let formatter = currencies.get(currency);
    if (!formatter) {
      formatter = new Intl.NumberFormat('en-IE', {
        style: 'currency',
        currency,
      });
      currencies.set(currency, formatter);
    }
    return formatter.format(value);
  } catch {
    return '—';
  }
};
export const formatCount = (value: number | null) =>
  value === null ? '—' : integer.format(value);
export const divide = (
  numerator: number | null,
  denominator: number | null,
  scale = 1
) =>
  numerator !== null && denominator !== null && denominator > 0
    ? (numerator / denominator) * scale
    : null;

export interface AdMetric {
  id: string;
  label: string;
  name: string;
  description: string;
  source: 'Meta' | 'Borradh';
  format: 'money' | 'count' | 'percent' | 'decimal';
  value: (ad: AdvertisingValues) => number | null;
}

export function describeMetric(
  metric: AdMetric,
  kind: AdvertisingAd['conversionKind'] | 'mixed'
): AdMetric {
  if (metric.id !== 'costConversion') return metric;
  if (kind === 'booking')
    return {
      ...metric,
      label: 'C/Book',
      name: 'Cost per conversion (booking)',
      description:
        'Amount spent divided by ad-attributed bookings made in Borradh during this period. One multi-service checkout counts as one booking. Holds, cancelled bookings, and calendar imports are excluded. Booking dates use UTC.',
    };
  if (kind === 'furtherClick')
    return {
      ...metric,
      label: 'CPFC',
      name: 'Cost per further click',
      description:
        'Amount spent divided by tracked booking links with a first human visit during this period, attributed to this ad. Used for external booking URLs. Repeat clicks and bot previews are excluded. Click dates use UTC.',
    };
  return metric;
}

const METRIC_DEFINITIONS: AdMetric[] = [
  {
    id: 'costConversion',
    label: 'C/Book / CPFC',
    name: 'Cost per conversion (booking) / Cost per further click',
    description:
      'C/Book uses bookings made in Borradh. CPFC uses first human visits to tracked booking links for organizations that use an external booking URL. Each row shows which measure applies.',
    source: 'Borradh',
    format: 'money',
    value: (ad) => divide(ad.spend, ad.conversions),
  },
  {
    id: 'bookings',
    label: 'Bookings',
    name: 'Ad-attributed Borradh bookings',
    description:
      'Bookings made in Borradh in this period, linked to this ad through a lead or tracked booking link. Multi-service checkouts count once; holds, cancellations, and calendar imports are excluded. Dates use UTC.',
    source: 'Borradh',
    format: 'count',
    value: (ad) => ad.bookings,
  },
  {
    id: 'furtherClicks',
    label: 'Further cl.',
    name: 'Tracked booking-link visits',
    description:
      'Tracked booking links with their first human visit in this period, attributed to this ad. Repeat clicks and bot previews are excluded. Dates use UTC.',
    source: 'Borradh',
    format: 'count',
    value: (ad) => ad.furtherClicks,
  },
  {
    id: 'spend',
    label: 'Spent',
    name: 'Amount spent',
    description:
      'Spend for the selected period, in the ad account’s own currency.',
    source: 'Meta',
    format: 'money',
    value: (ad) => ad.spend,
  },
  {
    id: 'impressions',
    label: 'Impr.',
    name: 'Impressions',
    description: 'Times the ad appeared on screen.',
    source: 'Meta',
    format: 'count',
    value: (ad) => ad.impressions,
  },
  {
    id: 'reach',
    label: 'Reach',
    name: 'Reach',
    description:
      'People who saw this ad at least once. Daily snapshots only support unique reach for a one-day view of one ad.',
    source: 'Meta',
    format: 'count',
    value: (ad) => ad.reach,
  },
  {
    id: 'frequency',
    label: 'Freq.',
    name: 'Frequency',
    description:
      'Average times each person saw the ad. Available for a one-day view of one ad; daily reach cannot be added across days.',
    source: 'Meta',
    format: 'decimal',
    value: (ad) => divide(ad.impressions, ad.reach),
  },
  {
    id: 'cpm',
    label: 'CPM',
    name: 'Cost per 1,000 impressions',
    description: 'Amount spent divided by impressions, multiplied by 1,000.',
    source: 'Meta',
    format: 'money',
    value: (ad) => divide(ad.spend, ad.impressions, 1000),
  },
  {
    id: 'clicks',
    label: 'Clicks',
    name: 'Clicks (all)',
    description:
      'All clicks on the ad, including clicks that do not open a link.',
    source: 'Meta',
    format: 'count',
    value: (ad) => ad.clicks,
  },
  {
    id: 'cpc',
    label: 'CPC',
    name: 'Cost per click (all)',
    description: 'Amount spent divided by all clicks.',
    source: 'Meta',
    format: 'money',
    value: (ad) => divide(ad.spend, ad.clicks),
  },
  {
    id: 'ctr',
    label: 'CTR',
    name: 'Click-through rate (all)',
    description: 'All clicks divided by impressions.',
    source: 'Meta',
    format: 'percent',
    value: (ad) => divide(ad.clicks, ad.impressions, 100),
  },
  {
    id: 'linkClicks',
    label: 'Link cl.',
    name: 'Link clicks',
    description: 'Clicks on links within the ad.',
    source: 'Meta',
    format: 'count',
    value: (ad) => ad.linkClicks,
  },
  {
    id: 'linkCpc',
    label: 'Link CPC',
    name: 'Cost per link click',
    description: 'Amount spent divided by link clicks.',
    source: 'Meta',
    format: 'money',
    value: (ad) => divide(ad.spend, ad.linkClicks),
  },
  {
    id: 'linkCtr',
    label: 'Link CTR',
    name: 'Link click-through rate',
    description: 'Link clicks divided by impressions.',
    source: 'Meta',
    format: 'percent',
    value: (ad) => divide(ad.linkClicks, ad.impressions, 100),
  },
  {
    id: 'landingViews',
    label: 'LPV',
    name: 'Landing page views',
    description: 'Times the destination page loaded after an ad click.',
    source: 'Meta',
    format: 'count',
    value: (ad) => ad.landingViews,
  },
  {
    id: 'costLanding',
    label: 'C/LPV',
    name: 'Cost per landing page view',
    description: 'Amount spent divided by landing page views.',
    source: 'Meta',
    format: 'money',
    value: (ad) => divide(ad.spend, ad.landingViews),
  },
  {
    id: 'conversations',
    label: 'Conv.',
    name: 'Ad-attributed conversations',
    description:
      'Conversations created in this period with a stored link to this ad, including lead-form attribution. Conversation dates use UTC.',
    source: 'Borradh',
    format: 'count',
    value: (ad) => ad.conversations,
  },
  {
    id: 'replied',
    label: 'Replied',
    name: 'Conversations with customer replies',
    description:
      'Conversations where the customer replied after the first business message.',
    source: 'Borradh',
    format: 'count',
    value: (ad) => ad.replied,
  },
  {
    id: 'replyRate',
    label: 'Reply %',
    name: 'Customer reply rate',
    description:
      'Conversations with a customer reply divided by all ad-attributed conversations.',
    source: 'Borradh',
    format: 'percent',
    value: (ad) => divide(ad.replied, ad.conversations, 100),
  },
  {
    id: 'costConversation',
    label: 'C/Conv.',
    name: 'Cost per conversation',
    description: 'Amount spent divided by ad-attributed Borradh conversations.',
    source: 'Borradh',
    format: 'money',
    value: (ad) => divide(ad.spend, ad.conversations),
  },
  {
    id: 'costReply',
    label: 'C/Reply',
    name: 'Cost per conversation with a reply',
    description: 'Amount spent divided by conversations with customer replies.',
    source: 'Borradh',
    format: 'money',
    value: (ad) => divide(ad.spend, ad.replied),
  },
  {
    id: 'metaConversations',
    label: 'Meta conv.',
    name: 'Messaging conversations started (Meta)',
    description:
      'Conversations started as reported by Meta. Attribution can differ from Borradh.',
    source: 'Meta',
    format: 'count',
    value: (ad) => ad.metaConversations,
  },
  {
    id: 'leads',
    label: 'Leads',
    name: 'Leads reported by Meta',
    description: 'Form and website lead actions attributed by Meta.',
    source: 'Meta',
    format: 'count',
    value: (ad) => ad.leads,
  },
  {
    id: 'cpl',
    label: 'CPL',
    name: 'Cost per lead',
    description: 'Amount spent divided by Meta-reported leads.',
    source: 'Meta',
    format: 'money',
    value: (ad) => divide(ad.spend, ad.leads),
  },
  {
    id: 'engagements',
    label: 'Eng.',
    name: 'Post engagements',
    description: 'Actions people took on the ad or post.',
    source: 'Meta',
    format: 'count',
    value: (ad) => ad.engagements,
  },
  {
    id: 'reactions',
    label: 'Reactions',
    name: 'Post reactions',
    description: 'Reactions to the ad post.',
    source: 'Meta',
    format: 'count',
    value: (ad) => ad.reactions,
  },
  {
    id: 'comments',
    label: 'Comments',
    name: 'Post comments',
    description: 'Comments on the ad post.',
    source: 'Meta',
    format: 'count',
    value: (ad) => ad.comments,
  },
  {
    id: 'shares',
    label: 'Shares',
    name: 'Post shares',
    description: 'Shares of the ad post.',
    source: 'Meta',
    format: 'count',
    value: (ad) => ad.shares,
  },
  {
    id: 'plays',
    label: 'Plays',
    name: 'Video plays',
    description: 'Times the video started playing.',
    source: 'Meta',
    format: 'count',
    value: (ad) => ad.plays,
  },
  {
    id: 'plays3s',
    label: '3s plays',
    name: 'Three-second video plays',
    description:
      'Video plays that reached three seconds, or the end for shorter videos.',
    source: 'Meta',
    format: 'count',
    value: (ad) => ad.plays3s,
  },
  {
    id: 'thruPlays',
    label: 'ThruPlay',
    name: 'ThruPlay video views',
    description:
      'Video plays to completion, or at least 15 seconds for longer videos.',
    source: 'Meta',
    format: 'count',
    value: (ad) => ad.thruPlays,
  },
  {
    id: 'costThruPlay',
    label: 'C/Thru',
    name: 'Cost per ThruPlay',
    description: 'Amount spent divided by ThruPlay video views.',
    source: 'Meta',
    format: 'money',
    value: (ad) =>
      ad.thruPlays === null ? null : divide(ad.spend, ad.thruPlays),
  },
  ...([25, 50, 75, 100] as const).map(
    (percent): AdMetric => ({
      id: `video${percent}`,
      label: `V${percent}%`,
      name: `Video plays at ${percent}% of length`,
      description: `Number of plays that reached ${percent}% of the video.`,
      source: 'Meta',
      format: 'count',
      value: (ad) => ad[`video${percent}`],
    })
  ),
];

export const PRIORITY_METRICS = [
  'costConversion',
  'cpl',
  'cpc',
  'replied',
  'replyRate',
];
export const AD_METRICS: AdMetric[] = [
  ...PRIORITY_METRICS.flatMap((id) =>
    METRIC_DEFINITIONS.filter((metric) => metric.id === id)
  ),
  ...METRIC_DEFINITIONS.filter(
    (metric) => !PRIORITY_METRICS.includes(metric.id)
  ),
];

export const METRIC_PRESETS: Record<
  string,
  { label: string; metrics: string[] }
> = {
  overview: {
    label: 'Overview',
    metrics: [
      ...PRIORITY_METRICS,
      'spend',
      'cpm',
      'ctr',
      'conversations',
      'costConversation',
    ],
  },
  delivery: {
    label: 'Delivery & clicks',
    metrics: [
      'spend',
      'impressions',
      'reach',
      'frequency',
      'cpm',
      'clicks',
      'cpc',
      'ctr',
      'linkClicks',
      'linkCpc',
      'linkCtr',
      'landingViews',
      'costLanding',
    ],
  },
  conversations: {
    label: 'Conversations',
    metrics: [
      ...PRIORITY_METRICS,
      'spend',
      'bookings',
      'furtherClicks',
      'conversations',
      'costConversation',
      'costReply',
      'metaConversations',
      'leads',
    ],
  },
  engagement: {
    label: 'Video & engagement',
    metrics: [
      'spend',
      'engagements',
      'reactions',
      'comments',
      'shares',
      'plays',
      'plays3s',
      'thruPlays',
      'costThruPlay',
      'video25',
      'video50',
      'video75',
      'video100',
    ],
  },
  all: {
    label: 'All metrics',
    metrics: AD_METRICS.map((metric) => metric.id),
  },
};

export function formatMetric(
  metric: AdMetric,
  value: number | null,
  currency: string | null = 'EUR'
) {
  if (value === null) return '—';
  if (metric.format === 'money') return formatMoney(value, currency);
  if (metric.format === 'percent') return `${value.toFixed(1)}%`;
  if (metric.format === 'decimal') return decimal.format(value);
  return formatCount(value);
}
